/**
 * Auth Service
 * Handles all authentication API calls
 * 
 * Authentication Flow:
 * 1. Frontend calls /api/v1/auth/login → Backend redirects to WorkOS
 * 2. User authenticates on WorkOS AuthKit (hosted UI)
 * 3. WorkOS redirects to /api/v1/auth/callback
 * 4. Backend creates session cookie, redirects to frontend
 * 5. Frontend checks session via /api/v1/auth/session
 */

import type { SessionResponse, UserResponse } from '@/types';

// API base URL - in production this is the same origin, in dev it might be different
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/**
 * Auth API endpoints
 * All endpoints are relative to the API base URL
 */
const AUTH_ENDPOINTS = {
  session: '/api/v1/auth/session',
  me: '/api/v1/auth/me',
  logout: '/api/v1/auth/logout',
  login: '/api/v1/auth/login',  // Redirects to WorkOS AuthKit
  devLogin: '/api/v1/auth/dev-login',  // Dev-only: creates session for local development
} as const;

/**
 * Check if user has a valid session
 * This is called on app load to determine auth state
 */
export async function checkSession(): Promise<SessionResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}${AUTH_ENDPOINTS.session}`, {
      method: 'GET',
      credentials: 'include', // Important: send cookies
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      return { authenticated: false };
    }

    const data: SessionResponse = await response.json();
    return data;
  } catch (error) {
    console.error('Session check failed:', error);
    return { authenticated: false };
  }
}

/**
 * Get current authenticated user's full information
 * Requires valid session
 */
export async function getCurrentUser(): Promise<UserResponse | null> {
  try {
    const response = await fetch(`${API_BASE_URL}${AUTH_ENDPOINTS.me}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        return null;
      }
      throw new Error(`Failed to get user: ${response.statusText}`);
    }

    const data: UserResponse = await response.json();
    return data;
  } catch (error) {
    console.error('Get current user failed:', error);
    return null;
  }
}

/**
 * Logout the current user
 * Clears session cookie on backend
 */
export async function logout(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}${AUTH_ENDPOINTS.logout}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response.ok;
  } catch (error) {
    console.error('Logout failed:', error);
    return false;
  }
}

/**
 * Get the login URL
 * Redirects to WorkOS AuthKit
 */
export function getLoginUrl(): string {
  return `${API_BASE_URL}${AUTH_ENDPOINTS.login}`;
}

/**
 * Redirect to login page
 * Used when session is invalid or expired
 */
export function redirectToLogin(): void {
  window.location.href = getLoginUrl();
}

/**
 * Dev-only: Create a development session
 * Only works in development environment
 * @param email Optional email (uses VITE_DEV_ADMIN_EMAIL if not provided)
 */
export async function devLogin(email?: string): Promise<boolean> {
  try {
    const emailParam = email || import.meta.env.VITE_DEV_ADMIN_EMAIL || '';
    const response = await fetch(`${API_BASE_URL}${AUTH_ENDPOINTS.devLogin}?email=${encodeURIComponent(emailParam)}`, {
      method: 'POST',
      credentials: 'include', // Important: receive cookies
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      console.error('Dev login failed:', response.statusText);
      return false;
    }

    const data = await response.json();
    console.log('✅ Dev session created:', data.message);
    
    // Note: HttpOnly cookies won't be visible in JavaScript (by design for security)
    // The browser will automatically set and send them on subsequent requests
    // We can't check response.headers.get('Set-Cookie') for HttpOnly cookies
    
    return true;
  } catch (error) {
    console.error('Dev login failed:', error);
    return false;
  }
}

/**
 * Auth service singleton
 */
export const authService = {
  checkSession,
  getCurrentUser,
  logout,
  getLoginUrl,
  redirectToLogin,
  devLogin,
};

export default authService;

